import { useState, useEffect, useRef } from 'react';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import ShopFnbFacilityBuildingOptionButton from '@/components/common/shop-fnb-facility/buttons/building-option';
import ShopFnbFacilityTriggerBuildingButton from '@/components/common/shop-fnb-facility/buttons/trigger-building';
import { useDropdownTTS, useFocusTrap } from '@/hooks';
import { useConfigStore } from '@/stores/configStore';

interface ShopFnbFacilityBuildingDropdownProps {
    defaultValue: string;
    options: {
        label: string;
        value: string;
    }[];
    onSelect: (value: string) => void;
}

const ShopFnbFacilityBuildingDropdown = ({ defaultValue, options, onSelect }: ShopFnbFacilityBuildingDropdownProps) => {
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string>(defaultValue);
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { speakOnFocus, speakOnSelection } = useDropdownTTS();

    const { trapFocus, releaseFocus } = useFocusTrap();

    useEffect(() => {
        if (isOpen) {
            trapFocus(dropdownRef.current, isOpen);
        } else {
            releaseFocus();
        }
    }, [isOpen, trapFocus, releaseFocus]);

    useEffect(() => {
        const handleClickOutside = (event: PointerEvent | TouchEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                event.preventDefault();
                event.stopPropagation();
                setIsOpen(false);
            }
        };

        if (isOpen) {
            // Delay focus to ensure any previous TTS is cancelled
            const firstOptionButton = dropdownRef.current?.querySelector('button[role="radio"]') as HTMLButtonElement;
            if (firstOptionButton) {
                firstOptionButton.focus();
                // Speak the first option using normal TTS logic
                speakOnFocus(firstOptionButton);
            }

            document.addEventListener('pointerdown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside, { passive: true });
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('pointerdown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, speakOnFocus]);

    return (
        <div className={styles['building-dropdown']} ref={dropdownRef} data-tts-scope="dropdown" data-region-nav>
            <ShopFnbFacilityTriggerBuildingButton
                tabIndex={9}
                data-interactive
                type="button"
                // role="button"
                title={options.find((option) => option.value === selectedValue)?.label || ''}
                aria-label={t('shop.search.tts.content.building_selection', {
                    buildingName: options.find((option) => option.value === selectedValue)?.label || '',
                })}
                aria-expanded={isOpen}
                isActive={isOpen}
                onClick={() => setIsOpen((prev) => !prev)}
                onFocus={(e) => {
                    speakOnFocus(e.target);
                }}
            />
            <div
                className={clsx(styles['building-dropdown__options'], {
                    [styles['building-dropdown__options--open']]: isOpen,
                    [styles['building-dropdown__options--close']]: !isOpen,
                    [styles['building-dropdown__options--contrast']]: contrast,
                })}
            >
                {options.map((option) => (
                    <ShopFnbFacilityBuildingOptionButton
                        tabIndex={isOpen ? 9 : -1}
                        data-interactive
                        key={option.value}
                        type="button"
                        role="radio"
                        data-role={t('common.data_role.button')}
                        aria-label={option.label}
                        onFocus={(e) => {
                            speakOnFocus(e.target);
                        }}
                        onClick={() => {
                            setSelectedValue(option.value);
                            onSelect(option.value);
                            setIsOpen(false);
                            speakOnSelection(option.label);
                        }}
                        title={option.label}
                        aria-checked={option.value === selectedValue}
                        disabled={!isOpen}
                    />
                ))}
            </div>
        </div>
    );
};

export default ShopFnbFacilityBuildingDropdown;
